import React, { useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import { Drone } from '../../types';
import { useStore } from '../../store';
import { degreesToCompass, formatDistance, formatFreq, formatSpeed } from '../../utils/helpers';
import './DroneCard.css';

const ADD_TO_WHITELIST = gql`
  mutation AddToWhitelist($droneId: String!) {
    addToWhitelist(droneId: $droneId) { id droneId }
  }
`;

const APPLY_COUNTERMEASURE = gql`
  mutation ApplyCountermeasure($droneId: String!, $action: CountermeasureAction!) {
    applyCountermeasure(droneId: $droneId, action: $action) { success message }
  }
`;

interface Props {
  drone: Drone;
  isWhitelisted?: boolean;
  onClick: () => void;
}

export default function DroneCard({ drone, isWhitelisted, onClick }: Props) {
  const [expanded, setExpanded] = useState(false);
  const alarmStatus = useStore(s => s.alarmedDrones).get(drone.droneId);
  const [addToWhitelist] = useMutation(ADD_TO_WHITELIST);
  const [applyCountermeasure] = useMutation(APPLY_COUNTERMEASURE);

  const sensorColor: Record<string, string> = {
    CRPC: '#4fc3f7', DRONE_ID: '#aed581', RID: '#ffb74d', CAMERA: '#f48fb1', WIDEBAND: '#ce93d8',
  };
  const color = sensorColor[drone.sensorType || ''] || '#90a4ae';

  const handleCountermeasure = async (action: string) => {
    try {
      await applyCountermeasure({ variables: { droneId: drone.droneId, action } });
      alert(`Command sent: ${action}`);
    } catch (e: any) {
      alert(e.message);
    }
  };

  return (
    <div className={`drone-card ${expanded ? 'expanded' : ''}`} onClick={() => { onClick(); setExpanded(!expanded); }}>
      <div className="drone-card-header">
        <div className={`drone-icon ${alarmStatus === "DEFENSE" ? "zone-defense" : alarmStatus === "WARNING" ? "zone-warning" : ""}`} style={{ borderColor: alarmStatus === "DEFENSE" ? "#ef5350" : alarmStatus === "WARNING" ? "#ffa726" : color, position: "relative" }}>✈️{isWhitelisted && <span className="wl-badge" title="Whitelisted">✓</span>}</div>
        <div className="drone-info">
          <div className={`drone-model ${alarmStatus === "DEFENSE" ? "text-defense" : alarmStatus === "WARNING" ? "text-warning" : ""}`}>{drone.model || 'Unknown'}</div>
          <div className="drone-id">{drone.droneId}</div>
        </div>
        <div className="drone-meta">
          <span className="sensor-tag" style={{ background: `${color}22`, color, borderColor: `${color}44` }}>
            {drone.sensorType}
          </span>
          <span className="drone-reliability">{((drone.reliability || 1) * 100).toFixed(0)}%</span>
        </div>
      </div>

      <div className="drone-stats">
        <div className="stat">
          <span className="stat-label">Freq</span>
          <span className="stat-value">{formatFreq(drone.frequencyMhz)}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Signal</span>
          <span className="stat-value">{drone.signalDb?.toFixed(1) ?? '—'} dB</span>
        </div>
        <div className="stat">
          <span className="stat-label">Dir</span>
          <span className="stat-value">{drone.directionDeg?.toFixed(0)}° ({degreesToCompass(drone.directionDeg)})</span>
        </div>
        <div className="stat">
          <span className="stat-label">Alt</span>
          <span className="stat-value">{drone.altitudeM ? `${drone.altitudeM.toFixed(0)}m` : '—'}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Dist</span>
          <span className="stat-value">{formatDistance(drone.distanceM)}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Speed</span>
          <span className="stat-value">{formatSpeed(drone.speedMs)}</span>
        </div>
      </div>

      {drone.lat && drone.lng && (
        <div className="drone-coords">
          📍 {drone.lat.toFixed(6)}, {drone.lng.toFixed(6)}
        </div>
      )}

      {expanded && (
        <div className="drone-actions" onClick={e => e.stopPropagation()}>
          <div className="actions-label">Counter-UAS Actions</div>
          <div className="actions-grid">
            <button className="action-btn danger" onClick={() => handleCountermeasure('NAVI_DEFENSE')}>
              🛡️ Navi Defense
            </button>
            <button className="action-btn warning" onClick={() => handleCountermeasure('EVICT')}>
              ↗️ Evict
            </button>
            <button className="action-btn" onClick={() => handleCountermeasure('LANDING')}>
              ⬇️ Force Land
            </button>
            <button className="action-btn" onClick={() => handleCountermeasure('AUTO_TRACKING')}>
              🎯 Track
            </button>
          </div>
          <button
            className="whitelist-btn"
            onClick={() => addToWhitelist({ variables: { droneId: drone.droneId } })}
          >
            ✅ Add to Whitelist
          </button>
        </div>
      )}
    </div>
  );
}
